import { observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type { PromptUnlinkDocumentOptions } from '@/features/documents'
import type { Document } from '@/features/documents/Document'
import type { DocumentStore } from '@/features/documents/DocumentStore'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import type { Id } from '@/infra/id'
import { ModalViewModel } from '@/infra/ModalViewModel'

/**
 * Confirms and unlinks a document from a job.
 */
export class UnlinkDocumentDialogViewModel {
  /**
   * View model for the underlying modal.
   */
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  /**
   * The document awaiting confirmation, and the job to unlink it from.
   */
  @observable private accessor _pending: {
    document: Document
    jobId: Id
  } | null = null

  constructor(
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _documentStore: DocumentStore,
  ) {}

  /**
   * Asks the user to confirm, and unlinks the document once they do.
   *
   * @returns Whether the document was unlinked.
   */
  async prompt({ document, jobId, signal }: PromptUnlinkDocumentOptions) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this._pending = { document, jobId }
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()
    return response ?? false
  }

  /**
   * Unlinks the document from the job.
   */
  unlink = task.resolved(async () => {
    if (!this._pending) {
      return
    }
    const { document, jobId } = this._pending

    const unlinked = await this._documentStore
      .removeDocumentLink(document, { type: 'Job', jobId })
      .then(() => true)
      .catch(this._createAlertErrorHandler('Unlinking the document failed'))
      .catch(() => false)
    if (!unlinked) {
      return
    }

    this._newAlert()
      .withState('success')
      .saying(`Success!  Document ${document.title} has been unlinked!`)
      .autoDismiss()
      .show()

    this._prompt.respond(true)
    this.modal.close()
  })
}
